import React, { useState, useEffect, useMemo, useCallback } from 'react';
import EvaluatorOpinionTable from './EvaluatorOpinionTable';
import NoteTabContent from './NoteDetailNoteTab';
import AdminInfoTab from './AdminInfoTab';
import EvaluationSummary from './EvaluationSummary';
import { MODAL_WIDTH } from '../const';
import { OpinionItemType, TokenStatus } from '../stores/data/types';
import { EvaluatorOpinionDto, OpinionItemDto, TokenEvaluationDto, TokenState, TweetStatsResponse } from '../api/fe-management-system-api/types';
import { feManagementSystemApi, retrospectiveApi } from '../api/fe-management-system-api';
import { useToasterStore } from '../stores/ui/toasterStore';
import getApiErrorMessage from '../utils/getApiErrorMessage';
import useEscapeKeyClose from '../hooks/useEscapeKeyClose';
import { useUIStore } from '../stores/ui/uiStore';
import { useDataStore } from '../stores/data/dataStore';
import TokenDetailHeader from './TokenDetailHeader';
import GmgnChart from './GmgnChart';
import TokenStatusHistory from './TokenStatusHistory';

interface NoteDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NoteDetailModal: React.FC<NoteDetailModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { showToaster } = useToasterStore();
  const { selectedToken } = useUIStore();
  const tokenAddress = selectedToken?.tokenAddress || '';
  const tokenData = useDataStore((state) => tokenAddress ? state.tokenStateMap[tokenAddress] : null);
  const fetchAndAddTokenState = useDataStore((state) => state.fetchAndAddTokenState);

  useEffect(() => {
    console.log('tokenAddress', tokenAddress);
    console.log('tokenData', tokenData);
    if (isOpen && tokenAddress && !tokenData) {
      fetchAndAddTokenState(tokenAddress);
    }
  }, [isOpen, tokenAddress, tokenData, fetchAndAddTokenState]);



  // Note 탭 상태
  const [noteContents, setNoteContents] = useState<string>(``);
  const [isEditingContents, setIsEditingContents] = useState<boolean>(false);
  const [tokenState, setTokenState] = useState<TokenState | null>(null);
  const [tweetStats, setTweetStats] = useState<TweetStatsResponse | null>(null);
  const [isLoadingTweetStats, setIsLoadingTweetStats] = useState(false);
  const tabs = ['Note', 'Admin Info'] as const;
  type TabOption = (typeof tabs)[number];
  const [activeTab, setActiveTab] = useState<TabOption>('Note');
  type TokenStateLoadOptions = {
    suppressErrorToast?: boolean;
    activeRef?: { current: boolean };
  };

  const loadTokenState = useCallback(
    async ({ suppressErrorToast = false, activeRef }: TokenStateLoadOptions = {}) => {
      if (!tokenAddress) {
        return null;
      }

      try {
        const state = await feManagementSystemApi.getTokenState(tokenAddress);
        if (activeRef && !activeRef.current) {
          return state;
        }

        setTokenState(state);
        return state;
      } catch (error: any) {
        console.error('Token state 조회 실패:', error);
        if (!suppressErrorToast) {
          showToaster(
            getApiErrorMessage(error, 'Failed to load token state.'),
            'error'
          );
        }
        return null;
      }
    },
    [tokenAddress, showToaster]
  );

  const fetchTweetStats = useCallback(async () => {
    if (!tokenAddress) return;
    setIsLoadingTweetStats(true);
    try {
      const stats = await feManagementSystemApi.getTweetStats(tokenAddress, 2);
      setTweetStats(stats);
    } catch (error) {
      console.error('트윗 통계 조회 실패:', error);
    } finally {
      setIsLoadingTweetStats(false);
    }
  }, [tokenAddress]);

  const refreshTokenStateAfterSave = useCallback(async () => {
    await loadTokenState({ suppressErrorToast: true });
  }, [loadTokenState]);

  const handleReportLoss = useCallback(async () => {
    if (!tokenAddress) return;
    try {
      await retrospectiveApi.reportLoss(tokenAddress);
      showToaster('Loss retrospective recorded successfully.', 'success');
      await refreshTokenStateAfterSave();
    } catch (error: any) {
      showToaster(
        getApiErrorMessage(error, 'Failed to record loss retrospective.'),
        'error',
      );
    }
  }, [refreshTokenStateAfterSave, showToaster, tokenAddress]);
  // EvaluatorOpinion 타입 정의
  type EvaluatorOpinion = {
    name: string;
    items: Array<{
      type: OpinionItemType;
      point: number;
      comments: string;
    }>;
    comment?: string;
  };

  const evaluationSource: TokenEvaluationDto | undefined = tokenState?.evaluation;
  const noteSource = tokenState?.note;

  const buildEvaluatorOpinions = useCallback((opinions?: EvaluatorOpinionDto[]): EvaluatorOpinion[] => {
    const existingOpinions: EvaluatorOpinionDto[] = opinions || [];

    const defaultNames = ['J', 'H', 'S'];
    const opinionMap = new Map<string, EvaluatorOpinion>(
      existingOpinions.map((op: EvaluatorOpinionDto) => [
        op.name,
        {
          name: op.name,
          items: op.items.map((item: OpinionItemDto) => ({
            type: item.type as OpinionItemType,
            point: item.score || 0,
            comments: item.comment || '',
          })),
          comment: op.comment,
        }
      ])
    );

    return defaultNames.map((name: string): EvaluatorOpinion => {
      const existing = opinionMap.get(name);
      if (existing) {
        return existing;
      }
      return {
        name,
        items: [
          { type: OpinionItemType.NS, point: 0, comments: '' },
          { type: OpinionItemType.ME, point: 0, comments: '' },
          { type: OpinionItemType.AQ, point: 0, comments: '' },
          { type: OpinionItemType.R, point: 0, comments: '' },
        ],
        comment: undefined,
      };
    });
  }, []);

  const initialEvaluatorOpinions = useMemo((): EvaluatorOpinion[] => {
    return buildEvaluatorOpinions(evaluationSource?.opinions);
  }, [evaluationSource?.opinions, buildEvaluatorOpinions]);

  const [evaluatorOpinions, setEvaluatorOpinions] = useState<EvaluatorOpinion[]>(initialEvaluatorOpinions);
  const [evaluationSummaryState, setEvaluationSummaryState] = useState(evaluationSource?.summary || '');
  const [evaluationRetrospectiveState, setEvaluationRetrospectiveState] = useState(evaluationSource?.retrospective || '');
  const [isNoteSaving, setIsNoteSaving] = useState(false);

  useEscapeKeyClose(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) {
      setTokenState(null);
      return;
    }
    if (!tokenAddress) {
      return;
    }

    const activeRef = { current: true };
    const refresh = async () => {
      await loadTokenState({ activeRef });
    };

    void refresh();

    return () => {
      activeRef.current = false;
    };
  }, [isOpen, tokenAddress, loadTokenState]);

  useEffect(() => {
    if (activeTab === 'Note' && tokenState?.note) {
      void fetchTweetStats();
    }
  }, [activeTab, fetchTweetStats, tokenState?.note]);

  const renderLeftGmgnChart = () => {
    if (!tokenAddress) {
      return null;
    }

    return (
      <div className="pointer-events-none absolute inset-y-0 left-[-950px] flex top-5 px-4 z-10">
        <div className="w-[360px] pointer-events-auto">
          <div className="rounded-2xl border border-gray-800 bg-gradient-to-br from-black via-gray-950 to-slate-900 p-2 shadow-2xl">
            <GmgnChart
              chain="sol"
              tokenAddress={tokenAddress}
              tokenSymbol={tokenData?.tokenInfo?.tokenSymbol}
              theme="dark"
              interval="15"
              width="900px"
              height="520px"
              className="w-full"
            />
          </div>
        </div>
      </div>
    );
  };

  // 기존 평가자 이름 목록 (API 호출 시 사용)
  const existingOpinionNames = useMemo((): string[] => {
    const opinions: EvaluatorOpinionDto[] = evaluationSource?.opinions || [];
    return opinions.map((op: EvaluatorOpinionDto) => op.name);
  }, [evaluationSource?.opinions]);
  
  const totalScoreSum = useMemo((): number => {
    const opinions: EvaluatorOpinionDto[] = evaluationSource?.opinions || [];
    return opinions.reduce((sum, op) => sum + (op.totalScore || 0), 0);
  }, [evaluationSource?.opinions]);

  // tokenData가 변경될 때 evaluatorOpinions 업데이트
  useEffect(() => {
    setEvaluatorOpinions(initialEvaluatorOpinions);
  }, [initialEvaluatorOpinions]);

  useEffect(() => {
    setEvaluationSummaryState(evaluationSource?.summary || '');
    setEvaluationRetrospectiveState(evaluationSource?.retrospective || '');
  }, [evaluationSource?.summary, evaluationSource?.retrospective]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const noteContentsFromToken = noteSource?.contents || '';
    setNoteContents(noteContentsFromToken);
    setIsEditingContents(false);
  }, [isOpen, noteSource?.contents]);

  const handleSaveEvaluationMeta = useCallback(
    async (nextSummary: string, nextRetrospective: string) => {
      try {
        let hasSaved = false;
        if (nextSummary !== evaluationSummaryState) {
          await feManagementSystemApi.updateEvaluationSummary(tokenAddress, nextSummary);
          setEvaluationSummaryState(nextSummary);
          hasSaved = true;
        }
        if (nextRetrospective !== evaluationRetrospectiveState) {
          await feManagementSystemApi.updateRetrospective(tokenAddress, nextRetrospective);
          setEvaluationRetrospectiveState(nextRetrospective);
          hasSaved = true;
        }
        showToaster('Evaluation saved successfully.', 'success');
        if (hasSaved) {
          await refreshTokenStateAfterSave();
        }
      } catch (error: any) {
        console.error('Failed to save evaluation meta:', error);
        showToaster(
          getApiErrorMessage(error, 'Failed to update evaluation. Please try again.'),
          'error'
        );
        throw error;
      }
    },
    [
      evaluationSummaryState,
      evaluationRetrospectiveState,
      refreshTokenStateAfterSave,
      tokenAddress,
      showToaster,
    ],
  );

  // totalScore 계산 함수 (NS + ME + AQ - R)
  const calculateTotalScore = (items: Array<{ type: OpinionItemType; point: number }>): number => {
    let ns = 0, me = 0, aq = 0, r = 0;
    items.forEach(item => {
      switch (item.type) {
        case OpinionItemType.NS:
          ns = item.point;
          break;
        case OpinionItemType.ME:
          me = item.point;
          break;
        case OpinionItemType.AQ:
          aq = item.point;
          break;
        case OpinionItemType.R:
          r = item.point;
          break;
      }
    });
    return ns + me + aq - r;
  };

  const handleUpdateEvaluatorOpinion = (index: number, updatedItems: Array<{ type: OpinionItemType; point: number; comments: string }>, comment?: string) => {
    const updated = [...evaluatorOpinions];
    updated[index] = {
      ...updated[index],
      items: updatedItems,
      comment: comment,
    };
    setEvaluatorOpinions(updated);
  };

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleStartEdit = () => {
    setIsEditingContents(true);
  };

  const handleSaveNote = async (nextContents: string) => {
    if (isNoteSaving) return;
    setIsNoteSaving(true);
    try {
      await feManagementSystemApi.updateTokenNote(tokenAddress, nextContents);
      setNoteContents(nextContents);
      setIsEditingContents(false);
      showToaster('Note saved successfully.', 'success');
      await refreshTokenStateAfterSave();
    } catch (error: any) {
      console.error('Failed to update note:', error);
      showToaster(
        getApiErrorMessage(error, 'Failed to save note. Please try again.'),
        'error'
      );
    } finally {
      setIsNoteSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={handleBackdropClick}
    >
      {/* 배경 오버레이 */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-30" 
        onClick={onClose} 
      />
      <div
        className="relative h-full bg-gray-900 border-l border-gray-700 shadow-2xl flex flex-col"
        style={{
          width: `${MODAL_WIDTH}px`,
          boxShadow: '-16px 0px 50px 20px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
         {renderLeftGmgnChart()}
        {/* 헤더 */}
        <TokenDetailHeader
          tokenAddress={tokenAddress}
          tokenData={tokenData}
          onClose={onClose}
          noteContents={noteSource?.contents ?? ''}
        />

        {/* 컨텐츠 */}
        <div className="overflow-y-auto flex-1">
          <div className="w-full bg-gray-900 p-4 flex items-center gap-4 fixed">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                  activeTab === tab
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="p-4 flex gap-4 h-full">
            <div className="flex-1 min-w-0 pt-[56px]">
              {activeTab === 'Note' ? (
                <NoteTabContent
                  tokenAddress={tokenAddress}
                  noteContents={noteContents}
                  isEditingContents={isEditingContents}
                  noteSource={noteSource}
                  isNoteSaving={isNoteSaving}
                  handleStartEdit={handleStartEdit}
                  handleSaveNote={handleSaveNote}
                  setIsEditingContents={setIsEditingContents}
                  tweetStats={tweetStats}
                  isLoadingTweetStats={isLoadingTweetStats}
                />
              ) : (
                <AdminInfoTab tokenAddress={tokenAddress} communityId={tokenState?.note?.twitterCommunityId || ''} defaultSort="time" />
              )}
            </div>
            <div className="w-[480px] flex flex-col gap-4 pt-[56px]">
              {/* 종합 평가 섹션 */}
              <EvaluationSummary
                totalScoreSum={totalScoreSum}
                summary={evaluationSummaryState}
                retrospective={evaluationRetrospectiveState}
                tokenState={tokenState ?? undefined}
                onSave={handleSaveEvaluationMeta}
                onReportLoss={handleReportLoss}
              />
              
              {/* 개별 평가자 의견 테이블들 */}
              {evaluatorOpinions.map((evaluatorOpinion, index) => (
                <EvaluatorOpinionTable
                  key={index}
                  name={evaluatorOpinion.name}
                  status={tokenState?.status || TokenStatus.NONE}
                  items={evaluatorOpinion.items}
                  totalScore={calculateTotalScore(evaluatorOpinion.items)}
                  comment={evaluatorOpinion.comment}
                  tokenAddress={tokenAddress}
                  existingOpinionNames={existingOpinionNames}
                  onUpdate={(updatedItems, comment) => handleUpdateEvaluatorOpinion(index, updatedItems, comment)}
                  onSaveSuccess={refreshTokenStateAfterSave}
                />
              ))}
              <TokenStatusHistory history={tokenState?.stateHistory} />
              
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoteDetailModal;
